import { GLOBAL_CONSTANTS } from 'assets/js/utils/constants.js'

const SELECTORS = {
  cursor: '.js-xray__cursor',
  image: '.js-xray__foreground',
  lens: '.js-xray__lens',
  viewButton: '.js-xray__view-button',
  mobileViewButton: '.js-xray__view-button--mobile',
  videoContainer: '.js-xray__video',
  video: '.js-xray__video-video',
  mobileCursor: '.js-xray__lens-cursor',
  description: '.js-xray__description',
  close: '.js-xray__close',
  closeButton: '.js-xray__close-button',
  skipButton: '.js-xray-skip-intro',
  is_expandable: 'is_expandable'
}

const CLASSES = {
  imageXrayContainer: 'xray__container',
  xrayResult: 'xray__cursor-result',
  xrayDisabled: 'xray__disabled',
  isHeader: 'js-minecraft-header'
}

class Xray {
  constructor(element) {
    this.element = element
    this.image = this.element.querySelector(SELECTORS.image)
    this.cursor = this.element.querySelector(SELECTORS.cursor)
    this.lens = this.element.querySelector(SELECTORS.lens)
    this.mobileCursor = this.element.querySelector(SELECTORS.mobileCursor)
    this.viewButton = this.element.querySelector(SELECTORS.viewButton)
    this.mobileViewButton = this.element.querySelector(SELECTORS.mobileViewButton)
    this.videoContainer = this.element.querySelector(SELECTORS.videoContainer)
    this.video = this.videoContainer.querySelector(SELECTORS.video)
    this.description = this.element.querySelector(SELECTORS.description)
    this.close = this.element.querySelector(SELECTORS.close)
    this.closeButton = this.element.querySelector(SELECTORS.closeButton)
    this.skipButton = this.element.querySelector(SELECTORS.skipButton)
    this.previousLocation = null
    this.emitter = GLOBAL_CONSTANTS.UTILS.EMITTER

    /* Add initial classes and xray properties: */
    this.element.classList.add(CLASSES.imageXrayContainer)
    this.referencePoint = null
    this.isXrayActive = false
    this.xrayEventsReady = false
    this.xrayDisabled = this.image.classList.contains(CLASSES.xrayDisabled) || false
    this.mobileTimeout = null
    this.lensSize = 288
    this.objectContain = this.element.dataset.objectContain || true
    this.viewButtonTop = 244
    this.viewButtonLeft = 0
    this.isMobile = false
    this.customPosition = false

    this.imageWidth = null

    this.cursorImg = new Image()
    this.cursorImg.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAUEBAAAACwAAAAAAQABAAACAkQBADs='

    this.init()
  }

  init() {
    /* Register event listeners: */
    this.registerEvents()

    const bgImg = this.image.dataset.bg
    this.cursor.style.backgroundImage = `url('${bgImg}')`
  }

  registerEvents() {
    if (this.skipButton) {
      this.skipButton.addEventListener('click', () => {
        this.video.currentTime = this.video.duration
      })

      this.video.onplay = () => {
        this.skipButton.classList.remove(GLOBAL_CONSTANTS.CLASSES.HIDE)
      }
    }

    this.video.onended = () => {
      this.videoEnded = true
      this.hideVideo()

      /* Set the xray container when video ends: */
      this.initImageXray()
      this.updateImageXray()
    }

    this.emitter.on(GLOBAL_CONSTANTS.EVENTS.XRAY_IS_DRAGGING, ({ element, isDragging }) => {
      if (this.element.contains(element) && this.viewButton) {
        if (isDragging) {
          this.viewButton.classList.add(GLOBAL_CONSTANTS.CLASSES.HIDE)
        } else {
          this.viewButton.classList.remove(GLOBAL_CONSTANTS.CLASSES.HIDE)
        }
      }
    })

    this.emitter.on(GLOBAL_CONSTANTS.EVENTS.IS_MOBILE, ({ isMobile }) => {
      if (isMobile) {
        this.objectContain = false
        this.isMobile = true
        this.lensSize = 209
        this.lensSize = 209

        if (this.videoEnded) {
          this.mobileCursor.classList.remove(GLOBAL_CONSTANTS.CLASSES.HIDE)
        }
      } else {
        this.objectContain = true
        this.lensSize = 288
        this.lensSize = 288
        this.initImageXray()
      }
    })

    /* Set the xray container on resize: */
    this.emitter.on(GLOBAL_CONSTANTS.EVENTS.RESIZE, () => this.updateImageXray())

    /* Drag & drop listeners need to be set on `document` instead of `this.image`,
      otherwise Firefox can't get x & y position of cursor.

      To allow a drop, prevent the default handling by cancelling both the
      dragenter and dragover events: */
    document.addEventListener('dragenter', (event) => event.preventDefault())

    /* Drag & drop events: */
    document.addEventListener('dragstart', (event) => {
      if (this.element.contains(event.target)) {
        this.hideDragIcon(event)
        this.emitter.emit(GLOBAL_CONSTANTS.EVENTS.XRAY_IS_DRAGGING, { element: this.element, isDragging: true })
      }
    }, false)

    document.addEventListener('dragover', (event) => {
      if (this.element.contains(event.target)) {
        event.preventDefault()
        this.handleDrag()
      }
    })

    document.addEventListener('drop', (event) => {
      if (this.element.contains(event.target)) {
        this.updateImageXray()
        this.emitter.emit(GLOBAL_CONSTANTS.EVENTS.XRAY_IS_DRAGGING, { element: this.element, isDragging: false })
      }
    })

    /* Touch events: */
    this.lens.addEventListener('touchstart', (event) => {
      event.preventDefault()
      this.hideDragIcon(event)
      this.emitter.emit(GLOBAL_CONSTANTS.EVENTS.XRAY_IS_DRAGGING, { element: this.element, isDragging: true })
    })

    this.lens.addEventListener('touchmove', (event) => {
      event.preventDefault()
      this.handleDrag()
      this.emitter.emit(GLOBAL_CONSTANTS.EVENTS.XRAY_IS_DRAGGING, { element: this.element, isDragging: false })
    })

    /* Mouse events: */
    document.addEventListener('mousemove', (event) => {
      if (this.element.contains(event.target)) {
        this.setCursor()
      }
    })

    /* Expand image if view button exists: */
    if (this.viewButton) {
      this.lens.addEventListener('click', () => {
        this.is_expandable = this.element.getElementsByClassName(SELECTORS.is_expandable)
        if (this.is_expandable.length > 0) {
          this.expandImage()
        }
      })
    }
    if (this.mobileViewButton) {
      this.mobileViewButton.addEventListener('click', () => {
        this.expandImage()
      })
    }
    if (this.close && this.closeButton) {
      this.closeButton.addEventListener('click', () => {
        this.closeView()
      })
    }

    this.emitter.on(GLOBAL_CONSTANTS.EVENTS.XRAY_IS_ACTIVE, ({ element, isActive }) => {
      if (this.element.contains(element)) {
        if (this.description) {
          if (isActive) {
            this.description.classList.remove(GLOBAL_CONSTANTS.CLASSES.HIDE)
            this.mobileCursor.classList.add(GLOBAL_CONSTANTS.CLASSES.HIDE)
          } else {
            this.description.classList.add(GLOBAL_CONSTANTS.CLASSES.HIDE)
            if (this.isMobile) {
              this.mobileCursor.classList.remove(GLOBAL_CONSTANTS.CLASSES.HIDE)
            }
          }
        }
      }
    })
  }

  hideVideo() {
    this.videoContainer.classList.add(GLOBAL_CONSTANTS.CLASSES.HIDE)
    this.cursor.classList.remove(GLOBAL_CONSTANTS.CLASSES.HIDE)

    if (this.viewButton) {
      this.viewButton.classList.remove(GLOBAL_CONSTANTS.CLASSES.HIDE)
    }

    if (this.mobileViewButton) {
      this.mobileViewButton.classList.remove(GLOBAL_CONSTANTS.CLASSES.HIDE)
    }

    if (this.isMobile) {
      this.mobileCursor.classList.remove(GLOBAL_CONSTANTS.CLASSES.HIDE)
    }
  }

  hideDragIcon(event) {
    event.dataTransfer.setDragImage(this.cursorImg, 0, 0)
  }

  /* Set lens and cursor position on load */
  initImageXray() {
    this.customPosition = this.lens.dataset.custom

    if (this.customPosition !== '') {
      const top = document.documentElement.clientHeight > this.element.clientHeight ? this.element.clientHeight * 0.5 - 200 : document.documentElement.clientHeight * 0.5 - 200
      const left = window.innerWidth * 0.5 - 180
      const right = this.element.clientWidth - left - this.lensSize
      const bottom = this.element.clientHeight - top - this.lensSize

      if (!this.objectContain) {
        /* On mobile, the left position of the cursor mimics `background-position: center`
          using the math below:
        */
        this.imageWidth = this.image.naturalWidth * (document.documentElement.clientHeight / this.image.naturalHeight)
      }

      this.lens.style.width = `${this.lensSize}px`
      this.lens.style.height = `${this.lensSize}px`
      this.lens.style.top = `${top}px`
      this.lens.style.left = `${left}px`
      this.lens.classList.remove(GLOBAL_CONSTANTS.CLASSES.ACTIVE)

      this.cursor.style.clipPath = `inset(${top}px ${right}px ${bottom}px ${left}px round 8px)`
      this.cursor.classList.remove(GLOBAL_CONSTANTS.CLASSES.ACTIVE)

      if (this.viewButton) {
        this.viewButton.style.top = top + this.viewButtonTop + 'px'
        this.viewButton.style.left = left + this.viewButtonLeft + 'px'
        this.viewButton.style.width = this.lensSize + 'px'
      }
    } else {
      const top = document.documentElement.clientHeight > this.element.clientHeight ? this.element.clientHeight * 0.5 : document.documentElement.clientHeight * 0.5
      const left = window.innerWidth * 0.5
      const right = this.element.clientWidth - left - this.lensSize
      const bottom = this.element.clientHeight - top - this.lensSize

      if (!this.objectContain) {
        /* On mobile, the left position of the cursor mimics `background-position: center`
          using the math below:
        */
        this.imageWidth = this.image.naturalWidth * (document.documentElement.clientHeight / this.image.naturalHeight)
      }

      this.lens.style.width = `${this.lensSize}px`
      this.lens.style.height = `${this.lensSize}px`
      this.lens.style.top = `${top}px`
      this.lens.style.left = `${left}px`
      this.lens.classList.remove(GLOBAL_CONSTANTS.CLASSES.ACTIVE)

      this.cursor.style.clipPath = `inset(${top}px ${right}px ${bottom}px ${left}px round 8px)`
      this.cursor.classList.remove(GLOBAL_CONSTANTS.CLASSES.ACTIVE)

      if (this.viewButton) {
        this.viewButton.style.top = top + this.viewButtonTop + 'px'
        this.viewButton.style.left = left + this.viewButtonLeft + 'px'
        this.viewButton.style.width = this.lensSize + 'px'
      }
    }
  }

  updateImageXray() {
    /* Set lens style: */
    this.lens.style.width = `${this.lensSize}px`
    this.lens.style.height = `${this.lensSize}px`

    /* Set background properties for the result DIV: */
    if (this.objectContain) {
      this.imageWidth = this.image.width
    } else {
      this.imageWidth = this.image.naturalWidth * (document.documentElement.clientHeight / this.image.naturalHeight)
    }

    this.cursor.classList.remove(GLOBAL_CONSTANTS.CLASSES.DRAGGING)

    const moveLens = () => this.moveLens()
    if (!this.xrayEventsReady) {
      this.image.addEventListener('dragover', () => this.moveLens())
      document.addEventListener('dragover', () => {
        if (this.element.contains(event.target)) {
          this.moveLens()
        }
      })

      /* Touch events */
      this.lens.addEventListener('touchmove', () => this.moveLens())
      this.xrayEventsReady = true
    }

    this.isXrayActive = true
    moveLens()
  }

  handleDrag() {
    if (!this.dragReady) {
      document.addEventListener('dragover', (event) => {
        if (this.element.contains(event.target)) {
          this.moveInitialCursor()
        }
      })
      this.lens.addEventListener('touchmove', () => this.moveInitialCursor())
      this.dragReady = true
    }

    if (this.mobileViewButton) {
      this.mobileViewButton.classList.remove(GLOBAL_CONSTANTS.CLASSES.HIDE)
    }

    if (!this.close.classList.contains(GLOBAL_CONSTANTS.CLASSES.HIDE)) {
      this.close.classList.add(GLOBAL_CONSTANTS.CLASSES.HIDE)
    }

    if (this.lens.classList.contains(GLOBAL_CONSTANTS.CLASSES.HIDE)) {
      this.lens.classList.remove(GLOBAL_CONSTANTS.CLASSES.HIDE)
    }

    this.emitter.emit(GLOBAL_CONSTANTS.EVENTS.XRAY_IS_ACTIVE, { element: this.element, isActive: false })
  }

  handleImageHover() {
    if (this.mobileTimeout !== null) {
      clearTimeout(this.mobileTimeout)
      this.mobileTimeout = null
      return
    }

    if (!this.cursor.classList.contains(CLASSES.xrayResult)) {
      this.updateImageXray()
      this.cursor.classList.add(CLASSES.xrayResult)
    }
  }

  expandImage() {
    this.previousLocation = this.cursor.style.clipPath

    this.cursor.style.clipPath = ''

    this.lens.classList.add(GLOBAL_CONSTANTS.CLASSES.HIDE)
    this.close.classList.remove(GLOBAL_CONSTANTS.CLASSES.HIDE)

    if (this.viewButton) {
      this.viewButton.classList.add(GLOBAL_CONSTANTS.CLASSES.HIDE)
    }

    if (this.mobileViewButton) {
      this.mobileViewButton.classList.add(GLOBAL_CONSTANTS.CLASSES.HIDE)
    }

    this.emitter.emit(GLOBAL_CONSTANTS.EVENTS.XRAY_IS_ACTIVE, { element: this.element, isActive: true })
  }

  closeView() {
    this.cursor.style.clipPath = this.previousLocation
    this.lens.classList.remove(GLOBAL_CONSTANTS.CLASSES.HIDE)
    this.close.classList.add(GLOBAL_CONSTANTS.CLASSES.HIDE)

    this.emitter.emit(GLOBAL_CONSTANTS.EVENTS.XRAY_IS_ACTIVE, { element: this.element, isActive: false })

    if (this.viewButton) {
      this.viewButton.classList.remove(GLOBAL_CONSTANTS.CLASSES.HIDE)
    }

    if (this.mobileViewButton) {
      this.mobileViewButton.classList.remove(GLOBAL_CONSTANTS.CLASSES.HIDE)
    }

    if (this.description) {
      this.description.classList.remove(GLOBAL_CONSTANTS.CLASSES.HIDE)
    }

    this.emitter.emit(GLOBAL_CONSTANTS.EVENTS.XRAY_IS_ACTIVE, { element: this.element, isActive: false })
  }

  moveLens(event) {
    /* Prevent any other actions that may occur when moving over the image */
    event && event.preventDefault()

    if (this.isXrayActive) {
      this.calculateReferencePoint()

      const top = this.referencePoint[1] - (this.lensSize / 2)
      const left = this.referencePoint[0] - (this.lensSize / 2)

      /* Move lens to match cursor, but don't exceed image border: */
      this.lens.style.top = `${top}px`
      this.lens.style.left = `${left}px`

      if (this.viewButton) {
        this.viewButton.style.top = `${top + this.viewButtonTop}px`
        this.viewButton.style.left = `${left + this.viewButtonLeft}px`
      }
    }
  }

  moveInitialCursor() {
    this.calculateReferencePoint()

    const top = this.referencePoint[1] - (this.lensSize / 2)
    const left = this.referencePoint[0] - (this.lensSize / 2)
    const right = this.element.clientWidth - left - this.lensSize
    const bottom = this.element.clientHeight - top - this.lensSize

    this.cursor.style.clipPath = `inset(${top}px ${right}px ${bottom}px ${left}px round 8px)`
    this.lens.style.cursor = 'grabbing'
    this.image.style.cursor = 'grabbing'
    this.cursor.classList.add(GLOBAL_CONSTANTS.CLASSES.DRAGGING)

    this.emitter.emit(GLOBAL_CONSTANTS.EVENTS.XRAY_IS_ACTIVE, { element: this.element, isActive: false })
  }

  calculateReferencePoint() {
    let lens = {}

    if (this.lens) {
      lens = this.lens
    } else {
      lens.offsetWidth = 0
      lens.offsetHeight = 0
    }

    /* Get the cursor's x and y positions: */
    const pos = this.getCursorPosition()

    /* Calculate the position of the lens: */
    const x = pos.x // an offset allows us to see the full image even when screen size is tight
    const y = pos.y

    this.referencePoint = [x, y]
  }

  getCursorPosition(event) {
    let x = 0; let y = 0
    event = event || window.event

    /* Get the x and y positions of the image: */
    const a = this.image.getBoundingClientRect()

    /* Calculate the cursor's x and y coordinates, relative to the image: */
    x = event ? event.pageX - a.left : 0
    y = event ? event.pageY - a.top : 0

    /* Consider any page scrolling: */
    x = x - window.pageXOffset
    y = y - window.pageYOffset

    return { x, y }
  }

  setCursor() {
    this.calculateReferencePoint()

    const mouseTop = this.referencePoint[1]
    const lensTopMin = this.lens.offsetTop
    const lensTopMax = this.lens.offsetTop + this.lensSize

    const mouseLeft = this.referencePoint[0]
    const lensLeftMin = this.lens.offsetLeft
    const lensLeftMax = this.lens.offsetLeft + this.lensSize

    if (mouseTop >= lensTopMin && mouseTop <= lensTopMax &&
      mouseLeft >= lensLeftMin && mouseLeft <= lensLeftMax) {
      this.lens.style.cursor = 'grab'
      this.image.style.cursor = 'grab'
    } else {
      this.image.style.cursor = 'initial'
    }
  }
}

export default {
  selector: '.js-xray',
  Source: Xray
}
